import { PREVIEW_OFFICIAL_ASSETS } from '../vnext/assets/previewOfficialAssets';
import { getErrorMessage } from '../utils/errorMessage';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTeamBrand } from '../vnext/branding/TeamBrandContext';
import {
  Archive,
  Check,
  ChevronDown,
  Download,
  FileAudio,
  FileVideo,
  Image as ImageIcon,
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import paymentApi from '../utils/paymentApi';
import { readPreviewAddedAssets, removePreviewAddedAssets, savePreviewOfficialAsset } from '../vnext/assets/previewAssetStore';
import LiquidCreateButton from '../vnext/components/LiquidCreateButton.tsx';
import PageTitleBlock from '../vnext/components/PageTitleBlock';
import { CONTENT_TAXONOMY_SCOPES, getContentTaxonomies } from '../vnext/contentTaxonomyApi';
import { videoFirstFrameSource } from '../vnext/mediaPreview';
import './AssetManagerPage.css';

const COPY = {
  uploadReusable: ['上传资产', 'Upload asset'],
  searchPlaceholder: ['搜索名称、角色或场景', 'Search names, characters, or scenes'],
  recentUpdated: ['最近更新', 'Recently updated'],
  recentCreated: ['最近创建', 'Recently created'],
  byName: ['按名称', 'Name'],
  batch: ['批量管理', 'Batch manage'],
  leaveBatch: ['退出批量', 'Exit batch'],
  loading: ['正在整理资产…', 'Organizing assets…'],
  empty: ['没有找到资产', 'No assets found'],
  emptyHelp: ['试试其他关键词或分类，也可以直接上传新的素材。', 'Try another search or category, or upload a new asset.'],
};

function unwrapLaravel(response, fallbackMessage) {
  const payload = response?.data;
  if (Number(payload?.code) !== 200) {
    if (Number(payload?.code) === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    throw new Error(payload?.message || fallbackMessage);
  }
  return payload?.data || {};
}

const PREVIEW_ASSETS = [
  {
    id: 'asset-yin-wuyue', type: 'character', type_name: '角色', name: '殷无月', description: '暗紫织金长裙，冷峻克制的血罗宫主人。',
    source: 'upload', updated_at: '2026-08-01T08:20:00Z',
    files: [{ id: 'file-yin-wuyue', url: '/images/vnext/assets/yin-wuyue.jpg', mime: 'image/jpeg', kind: 'three_view', volc_status: 'Active' }],
  },
  {
    id: 'asset-ye-changge', type: 'character', type_name: '角色', name: '叶长歌', description: '白衣银饰，清冷通透的仙门剑修。',
    source: 'upload', updated_at: '2026-08-01T08:10:00Z',
    files: [{ id: 'file-ye-changge', url: '/images/vnext/assets/ye-changge.jpg', mime: 'image/jpeg', kind: 'three_view', volc_status: 'Active' }],
  },
  {
    id: 'asset-chen-qi', type: 'character', type_name: '角色', name: '陈七', description: '灰蓝布衣，沉静可靠的江湖少年。',
    source: 'upload', updated_at: '2026-08-01T08:00:00Z',
    files: [{ id: 'file-chen-qi', url: '/images/vnext/assets/chen-qi.jpg', mime: 'image/jpeg', kind: 'three_view', volc_status: 'Processing' }],
  },
  {
    id: 'asset-su-zhaoying', type: 'character', type_name: '角色', name: '苏照影', description: '红衣金饰，明艳灵动的古装少女。',
    source: 'upload', updated_at: '2026-08-01T07:50:00Z',
    files: [{ id: 'file-su-zhaoying', url: '/images/vnext/assets/su-zhaoying.jpg', mime: 'image/jpeg', kind: 'three_view', volc_status: null }],
  },
  {
    id: 'asset-xueluo-room', type: 'scene', type_name: '场景', name: '血罗宫密室', description: '潮湿石壁、破碎药碗与从高处落下的冷光。',
    source: 'upload', updated_at: '2026-08-01T07:40:00Z',
    files: [{ id: 'file-xueluo-room', url: '/images/vnext/assets/xueluo-secret-room.jpg', mime: 'image/jpeg', kind: 'other', volc_status: null }],
  },
];



function roleStatus(asset) {
  const statuses = (asset.files || []).map(file => file.volc_status).filter(Boolean);
  if (statuses.includes('Active')) return 'ready';
  if (statuses.includes('Processing')) return 'processing';
  if (statuses.includes('Failed')) return 'failed';
  return 'idle';
}

const ROLE_STATUS = {
  ready: { label: '可用于视频生成', labelEn: 'Ready for video', className: 'is-ready' },
  processing: { label: '正在准备', labelEn: 'Preparing', className: 'is-processing' },
  failed: { label: '准备失败', labelEn: '准备失败', className: 'is-failed' },
  idle: { label: '尚未准备', labelEn: 'Not prepared', className: 'is-idle' },
};

function mediaKind(file, assetType = '') {
  const mime = String(file?.mime || '').toLowerCase();
  const url = String(file?.url || '').split(/[?#]/)[0].toLowerCase();
  if (mime.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi|mkv)$/.test(url) || assetType === 'clip') return 'video';
  if (mime.startsWith('audio/') || /\.(mp3|wav|m4a|aac|ogg|flac)$/.test(url) || assetType === 'voice') return 'audio';
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp|gif|avif|bmp)$/.test(url)) return 'image';
  return 'other';
}

function mediaIcon(asset) {
  const kind = mediaKind(asset.files?.[0], asset.type);
  if (kind === 'audio') return FileAudio;
  if (kind === 'video') return FileVideo;
  return ImageIcon;
}

function assetTypesFromItems(items) {
  return [...new Map(items.map(item => [item.type, { code: item.type, name: item.type_name || item.type }])).values()];
}

function assetTypeName(asset, assetTypes) {
  return asset.type_name || assetTypes.find(item => item.code === asset.type)?.name || asset.type || '资产';
}

function AssetPreview({ asset, typeName, compact = false, interactive = false }) {
  const first = asset.files?.[0];
  const MediaIcon = mediaIcon(asset);
  const coverLike = !interactive && asset.cover;
  const kind = mediaKind(first, asset.type);
  const imageLike = first?.url && kind === 'image';
  const videoLike = first?.url && kind === 'video';
  const audioLike = first?.url && kind === 'audio';

  return (
    <div className={`asset-media ${compact ? 'is-compact' : ''} ${asset.type === 'character' ? 'is-character' : ''}`}>
      {coverLike ? (
        <img src={asset.cover} alt="" />
      ) : imageLike ? (
        <img src={first.url} alt="" />
      ) : videoLike ? (
        <video src={interactive ? first.url : videoFirstFrameSource(first.url)} poster={interactive && asset.cover ? asset.cover : undefined} controls={interactive} muted={!interactive} playsInline preload="metadata" aria-hidden={!interactive || undefined} />
      ) : interactive && audioLike ? (
        <div className="asset-media-placeholder"><FileAudio size={24} /><audio src={first.url} controls preload="metadata" /></div>
      ) : (
        <div className="asset-media-placeholder">
          <MediaIcon size={compact ? 22 : 30} strokeWidth={1.45} />
          <span>{typeName || asset.type_name || asset.type || '资产'}</span>
        </div>
      )}
    </div>
  );
}

function AssetSelect({ value, onChange, options, ariaLabel, className = '' }) {
  const [open, setOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();
  const selected = options.find(option => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeWithEscape = event => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeWithEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, [open]);

  const moveSelection = direction => {
    const currentIndex = Math.max(0, options.findIndex(option => option.value === value));
    const nextIndex = (currentIndex + direction + options.length) % options.length;
    onChange(options[nextIndex].value);
  };

  const openMenu = () => {
    const bounds = rootRef.current?.getBoundingClientRect();
    const menuHeight = Math.min(options.length * 40 + 14, 292);
    const roomBelow = bounds ? window.innerHeight - bounds.bottom : menuHeight;
    const roomAbove = bounds?.top || 0;
    setOpensUp(roomBelow < menuHeight + 12 && roomAbove > roomBelow);
    setOpen(true);
  };

  return (
    <div ref={rootRef} className={`asset-select ${open ? 'is-open' : ''} ${opensUp ? 'opens-up' : ''} ${className}`}>
      <button
        type="button"
        className="asset-select-trigger"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={menuId}
        onClick={() => {
          if (open) setOpen(false);
          else openMenu();
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            moveSelection(event.key === 'ArrowDown' ? 1 : -1);
            if (!open) openMenu();
          }
        }}
      >
        <span>{selected?.label}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <div id={menuId} className="asset-select-menu" role="listbox" aria-label={ariaLabel}>
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? 'is-selected' : ''}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.value === value && <Check size={15} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AssetManagerPage({ preview: previewProp = false }) {
  const brand = useTeamBrand();
  const outlet = useOutletContext() || {};
  const preview = previewProp || outlet.preview;
  const english = outlet.language === 'en-US';
  const tx = key => COPY[key]?.[english ? 1 : 0] || key;
  const [scope, setScope] = useState('mine');
  const [type, setType] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('updated');
  const [assets, setAssets] = useState(() => preview ? [...readPreviewAddedAssets(), ...PREVIEW_ASSETS] : []);
  const [assetTypes, setAssetTypes] = useState(() => preview ? assetTypesFromItems([...PREVIEW_ASSETS, ...PREVIEW_OFFICIAL_ASSETS]) : []);
  const [mineCount, setMineCount] = useState(() => preview ? readPreviewAddedAssets().length + PREVIEW_ASSETS.length : 0);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [batchMode, setBatchMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [busy, setBusy] = useState('');
  const [savedOfficial, setSavedOfficial] = useState(() => new Set(preview ? readPreviewAddedAssets().map(asset => asset.official_asset_id) : []));
  const uploadInputRef = useRef(null);
  const uploadTypeOptions = assetTypes.map(item => ({ value: item.code, label: item.name }));

  const loadTypes = useCallback(async () => {
    if (preview) return;
    try {
      const nextTypes = await getContentTaxonomies(CONTENT_TAXONOMY_SCOPES.assets);
      setAssetTypes(nextTypes);
      setType(current => current === 'all' || nextTypes.some(item => item.code === current) ? current : 'all');
      setUploadType(current => nextTypes.some(item => item.code === current) ? current : (nextTypes[0]?.code || ''));
    } catch (requestError) {
      setAssetTypes([]);
      setError(getErrorMessage(requestError, '资产类型加载失败'));
    }
  }, [preview]);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  useEffect(() => {
    if (!uploadType && assetTypes.length) setUploadType(assetTypes[0].code);
  }, [assetTypes, uploadType]);

  useEffect(() => () => {
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
  }, [uploadPreviewUrl]);

  const chooseUploadFile = file => {
    setUploadFile(file);
    setUploadResult(null);
    setUploadPreviewUrl(file ? URL.createObjectURL(file) : '');
    if (file && !uploadName) setUploadName(file.name.replace(/\.[^.]+$/, ''));
  };

  const resetUploadForm = () => {
    setUploadName('');
    setUploadType(assetTypes[0]?.code || '');
    setUploadFile(null);
    setUploadPreviewUrl('');
    setUploadResult(null);
  };

  const closeUploadDialog = () => {
    setUploadDialog(false);
    resetUploadForm();
  };
  const officialMode = scope === 'official';
  const sortOptions = officialMode
    ? [
        { value: 'updated', label: english ? 'Popular' : '热门推荐' },
        { value: 'created', label: english ? 'Newest' : '最新上架' },
        { value: 'name', label: tx('byName') },
      ]
    : [
        { value: 'updated', label: tx('recentUpdated') },
        { value: 'created', label: tx('recentCreated') },
        { value: 'name', label: tx('byName') },
      ];

  const load = useCallback(async () => {
    if (preview) return;
    setLoading(true);
    setError('');
    try {
      const params = { sort };
      if (query.trim()) params.q = query.trim();
      if (type !== 'all') params.type = type;
      const assetResponse = await paymentApi.get(officialMode ? '/asset/official' : '/asset/list', { params });
      const data = unwrapLaravel(assetResponse, '资产加载失败，请稍后重试');
      const nextAssets = Array.isArray(data.list) ? data.list : [];
      setAssets(nextAssets);
      if (officialMode) {
        setSavedOfficial(new Set(nextAssets.filter(asset => asset.added).map(asset => asset.id)));
      } else {
        setMineCount(nextAssets.length);
      }
    } catch (e) {
      setError(getErrorMessage(e, '资产加载失败，请稍后重试'));
    } finally {
      setLoading(false);
    }
  }, [officialMode, preview, query, sort, type]);

  useEffect(() => {
    const timer = window.setTimeout(load, query ? 260 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);

  const visibleAssets = useMemo(() => {
    if (!preview) return assets;
    const keyword = query.trim().toLocaleLowerCase('zh-CN');
    const sourceAssets = officialMode ? PREVIEW_OFFICIAL_ASSETS : assets;
    return sourceAssets
      .filter(asset => type === 'all' || asset.type === type)
      .filter(asset => !keyword || `${asset.name} ${asset.description}`.toLocaleLowerCase('zh-CN').includes(keyword))
      .sort((a, b) => sort === 'name'
        ? a.name.localeCompare(b.name, 'zh-CN')
        : new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  }, [assets, officialMode, preview, query, sort, type]);

  const switchScope = nextScope => {
    setScope(nextScope);
    setBatchMode(false);
    setSelected(new Set());
    setDetail(null);
    setType('all');
    setSort('updated');
  };

  const addOfficialAsset = async asset => {
    if (savedOfficial.has(asset.id)) return;
    setBusy(`official-${asset.id}`);
    setNotice('');
    try {
      if (preview) {
        const saved = savePreviewOfficialAsset(asset);
        setSavedOfficial(current => new Set(current).add(asset.id));
        setAssets(current => current.some(item => item.id === saved.id) ? current : [saved, ...current]);
      } else {
        const response = await paymentApi.post('/asset/official/add', { id: asset.id });
        const data = unwrapLaravel(response, brand.text('添加小影资产失败'));
        setSavedOfficial(current => new Set(current).add(asset.id));
        setAssets(current => current.map(item => item.id === asset.id ? { ...item, added: true } : item));
        if (!data.already_added) setMineCount(current => current + 1);
      }
      setNotice('已添加到“我的资产”');
    } catch (e) {
      setError(getErrorMessage(e, brand.text('添加小影资产失败')));
    } finally {
      setBusy('');
    }
  };

  const toggleSelected = assetId => {
    setSelected(current => {
      const next = new Set(current);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const openAsset = asset => {
    if (batchMode && !officialMode) toggleSelected(asset.id);
    else setDetail(asset);
  };

  const archiveSelected = async ids => {
    const targets = ids || [...selected];
    if (!targets.length) return;
    setBusy('archive');
    setNotice('');
    try {
      if (preview) {
        removePreviewAddedAssets(targets);
        setAssets(current => current.filter(asset => !targets.includes(asset.id)));
      }
      else {
        unwrapLaravel(await paymentApi.post('/asset/archive', { ids: targets }), '归档资产失败');
        await load();
      }
      setSelected(new Set());
      setBatchMode(false);
      setDetail(null);
      setNotice(`已归档 ${targets.length} 项资产`);
    } catch (e) {
      setError(getErrorMessage(e, '归档资产失败'));
    } finally {
      setBusy('');
    }
  };

  const uploadAsset = async () => {
    if (!uploadFile) return;
    setBusy('upload');
    setNotice('');
    try {
      const name = uploadName.trim() || uploadFile.name.replace(/\.[^.]+$/, '');
      let uploadedAsset;
      if (preview) {
        uploadedAsset = {
          id: `asset-${Date.now()}`, name, type: uploadType, source: 'upload',
          description: '', updated_at: new Date().toISOString(),
          files: [{ id: `file-${Date.now()}`, url: URL.createObjectURL(uploadFile), mime: uploadFile.type, volc_status: null }],
        };
        setAssets(current => [uploadedAsset, ...current]);
      } else {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('name', name);
        formData.append('type', uploadType);
        uploadedAsset = unwrapLaravel(await paymentApi.post('/asset/upload', formData, {
          timeout: 120000,
          headers: { 'Content-Type': 'multipart/form-data' },
        }), '上传资产失败');
        await load();
      }
      setUploadResult(uploadedAsset?.asset || uploadedAsset);
      setNotice('资产上传成功，已保存到 OSS');
    } catch (e) {
      setError(getErrorMessage(e, '上传资产失败'));
    } finally {
      setBusy('');
    }
  };

  const prepareRole = async asset => {
    const file = asset.files?.find(item => item.volc_status !== 'Active');
    if (!file) return;
    setBusy(`role-${file.id}`);
    try {
      if (preview) {
        setAssets(current => current.map(item => item.id === asset.id
          ? { ...item, files: item.files.map(candidate => candidate.id === file.id ? { ...candidate, volc_status: 'Active' } : candidate) }
          : item));
        setDetail(current => current?.id === asset.id
          ? { ...current, files: current.files.map(candidate => candidate.id === file.id ? { ...candidate, volc_status: 'Active' } : candidate) }
          : current);
      }
    } catch (e) {
      setError(getErrorMessage(e, '角色准备失败'));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="asset-center-page vnext-page-surface">
      <header className="asset-center-header">
        <PageTitleBlock
          title={english ? 'Assets' : '资产中心'}
        />
        {!officialMode && <div className="asset-header-actions">
          <LiquidCreateButton
            variant="lavender"
            label={tx('uploadReusable')}
            leadingIcon={<Upload size={16} />}
            showArrow={false}
            onClick={() => setUploadDialog(true)}
          />
        </div>}
      </header>

      <div className="asset-scope-row">
        <div className="asset-scope-tabs" role="tablist" aria-label={english ? 'Asset spaces' : '资产空间'}>
          <button type="button" role="tab" aria-selected={scope === 'mine'} className={scope === 'mine' ? 'is-active' : ''} onClick={() => switchScope('mine')}>
            {english ? 'My assets' : '我的资产'} <span>{preview ? assets.length : mineCount}</span>
          </button>
          <button type="button" role="tab" aria-selected={scope === 'official'} className={scope === 'official' ? 'is-active' : ''} onClick={() => switchScope('official')}>
            {brand.text(english ? 'Xiaoying library' : '小影资产库')}
          </button>
        </div>
      </div>

      <section className="asset-toolbar">
        <label className="asset-search">
          <Search size={17} />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder={officialMode ? brand.text(english ? 'Search Xiaoying assets' : '搜索小影资产') : tx('searchPlaceholder')} aria-label={english ? 'Search assets' : '搜索资产'} />
          {query && <button type="button" onClick={() => setQuery('')} aria-label={english ? 'Clear search' : '清空搜索'}><X size={14} /></button>}
        </label>

        <div className="asset-type-tabs" role="radiogroup" aria-label="资产类型">
          {[{ code: 'all', name: english ? 'All' : '全部' }, ...assetTypes].map(item => (
            <button
              key={item.code}
              type="button"
              role="radio"
              aria-checked={type === item.code}
              className={type === item.code ? 'is-active' : ''}
              onClick={() => setType(item.code)}
            >
              {item.name}
            </button>
          ))}
        </div>

        <div className="asset-toolbar-tail">
          <AssetSelect
            value={sort}
            onChange={setSort}
            options={sortOptions}
            ariaLabel={english ? 'Sort assets' : '资产排序'}
            className="asset-sort-select"
          />
          {!officialMode && (
            <button type="button" className={`asset-batch-trigger ${batchMode ? 'is-active' : ''}`} onClick={() => { setBatchMode(value => !value); setSelected(new Set()); }}>
              {batchMode ? tx('leaveBatch') : tx('batch')}
            </button>
          )}
        </div>
      </section>

      {notice && <div className="asset-notice" role="status">{notice}<button type="button" onClick={() => setNotice('')}><X size={14} /></button></div>}
      {error && <div className="asset-error" role="alert">{error}<button type="button" onClick={() => setError('')}><X size={14} /></button></div>}

      {batchMode && !officialMode && (
        <section className="asset-batch-bar">
          <button type="button" onClick={() => setSelected(new Set(visibleAssets.map(asset => asset.id)))}>
            <Check size={15} /> {english ? 'Select all' : '全选'}
          </button>
          <span>{english ? `${selected.size} selected` : `已选 ${selected.size} 项`}</span>
          <button type="button" className="is-danger" disabled={!selected.size || busy === 'archive'} onClick={() => archiveSelected()}>
            <Archive size={15} /> {english ? 'Archive' : '归档'}
          </button>
        </section>
      )}

      {loading ? (
        <div className="asset-loading"><Loader2 className="spin" size={22} />{tx('loading')}</div>
      ) : visibleAssets.length === 0 ? (
        <div className="asset-empty">
          <div><ImageIcon size={28} /></div>
          <h2>{tx('empty')}</h2>
          <p>{tx('emptyHelp')}</p>
        </div>
      ) : (
        <div className="asset-grid">
          {visibleAssets.map(asset => {
            const status = ROLE_STATUS[roleStatus(asset)];
            const isSelected = selected.has(asset.id);
            return (
              <article
                key={asset.id}
                className={`asset-card ${officialMode ? 'is-official' : ''} ${isSelected ? 'is-selected' : ''}`}
                onClick={() => openAsset(asset)}
                onKeyDown={event => { if (event.key === 'Enter') openAsset(asset); }}
                tabIndex={0}
              >
                <div className="asset-card-media">
                  <AssetPreview asset={asset} typeName={assetTypeName(asset, assetTypes)} />
                  {batchMode && <span className="asset-card-check">{isSelected && <Check size={14} />}</span>}
                  {preview && asset.type === 'character' && !officialMode
                    ? <span className={`asset-role-badge ${status.className}`}><ShieldCheck size={12} />{english ? status.labelEn : status.label}</span>
                    : <span className="asset-type-badge">{assetTypeName(asset, assetTypes)}</span>}
                </div>
                <div className="asset-card-body">
                  <div className="asset-card-title-row">
                    <h3>{asset.name}</h3>
                    {officialMode ? (
                      <button
                        type="button"
                        className={`asset-official-add ${savedOfficial.has(asset.id) ? 'is-saved' : ''}`}
                        aria-label={savedOfficial.has(asset.id) ? `${asset.name}已添加` : `添加${asset.name}到我的资产`}
                        disabled={savedOfficial.has(asset.id) || busy === `official-${asset.id}`}
                        onClick={event => { event.stopPropagation(); addOfficialAsset(asset); }}
                      >
                        {busy === `official-${asset.id}` ? <Loader2 className="spin" size={14} /> : savedOfficial.has(asset.id) ? <Check size={14} /> : <Plus size={14} />}
                        {busy === `official-${asset.id}` ? (english ? 'Adding' : '添加中') : savedOfficial.has(asset.id) ? (english ? 'Added' : '已添加') : (english ? 'Add' : '添加')}
                      </button>
                    ) : (
                      <button type="button" aria-label={english ? `More actions for ${asset.name}` : `${asset.name}更多操作`} onClick={event => { event.stopPropagation(); setDetail(asset); }}><MoreHorizontal size={17} /></button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {detail && (
        <>
          <button type="button" className="asset-drawer-backdrop" aria-label="关闭资产详情" onClick={() => setDetail(null)} />
          <aside className="asset-detail-drawer" aria-label={english ? 'Asset details' : '资产详情'}>
            <header><div><span>{assetTypeName(detail, assetTypes)}</span><h2>{detail.name}</h2></div><button type="button" onClick={() => setDetail(null)} aria-label={english ? 'Close' : '关闭'}><X size={18} /></button></header>
            <AssetPreview asset={detail} typeName={assetTypeName(detail, assetTypes)} compact interactive />
            <p className="asset-detail-description">{detail.description || (english ? 'Add character traits, scene mood, or reuse notes.' : '可以补充角色特征、场景气质或复用注意事项。')}</p>

            {preview && detail.type === 'character' && detail.source !== 'official' && (() => {
              const status = ROLE_STATUS[roleStatus(detail)];
              return (
                <section className="asset-role-panel">
                  <div><ShieldCheck size={18} /><span><strong>{english ? status.labelEn : status.label}</strong><small>{english ? 'Prepare character references before using them for video generation.' : '角色参考图准备完成后，才能稳定用于视频生成。'}</small></span></div>
                  {roleStatus(detail) !== 'ready' && (
                    <button type="button" disabled={busy.startsWith('role-')} onClick={() => prepareRole(detail)}>
                      {busy.startsWith('role-') ? <Loader2 className="spin" size={14} /> : <Sparkles size={14} />}
                      {english ? 'Prepare for video' : '准备视频生成'}
                    </button>
                  )}
                </section>
              );
            })()}

            <section className="asset-detail-section">
              <div className="asset-detail-section-title"><h3>{english ? 'Asset files' : '资产文件'}</h3><span>{detail.files?.length || 0}</span></div>
              <div className="asset-file-list">
                {(detail.files || []).map((file, index) => (
                  <div key={file.id || index}>
                    <AssetPreview asset={{ ...detail, cover: '', files: [file] }} typeName={assetTypeName(detail, assetTypes)} compact />
                    <span><strong>{file.kind === 'full_body' ? (english ? 'Full-body reference' : '全身参考') : file.kind === 'face_closeup' ? (english ? 'Face close-up' : '面部特写') : `${english ? 'File' : '文件'} ${index + 1}`}</strong><small>{file.mime || (english ? 'Media file' : '媒体文件')}</small></span>
                    {file.url && <a href={file.url} target="_blank" rel="noreferrer" aria-label={english ? `Download ${detail.name}` : `下载${detail.name}`}><Download size={15} /></a>}
                  </div>
                ))}
              </div>
            </section>

            <section className="asset-detail-section asset-detail-meta">
              <h3>{english ? 'Where to use it' : '使用位置'}</h3>
              <p>{detail.source === 'official'
                ? (english ? 'Add it to My assets, then choose it from any canvas.' : '添加到“我的资产”后，可以在任意画布中直接选择。')
                : (english ? 'Choose it from the Assets panel inside a canvas.' : '在画布内打开资产面板，直接选择即可。')}</p>
            </section>

            <footer className="asset-detail-actions">
              {detail.source === 'official' ? (
                <button
                  type="button"
                  className="is-primary"
                  disabled={savedOfficial.has(detail.id)}
                  onClick={() => addOfficialAsset(detail)}
                >
                  {savedOfficial.has(detail.id) ? <Check size={15} /> : <Plus size={15} />}
                  {savedOfficial.has(detail.id) ? (english ? 'Added to My assets' : '已添加到我的资产') : (english ? 'Add to My assets' : '添加到我的资产')}
                </button>
              ) : (
                <button type="button" className="is-danger" disabled={busy === 'archive'} onClick={() => archiveSelected([detail.id])}><Archive size={15} />{english ? 'Archive asset' : '归档资产'}</button>
              )}
            </footer>
          </aside>
        </>
      )}

      {uploadDialog && (
        <div className="asset-modal-layer" role="dialog" aria-modal="true" aria-label={tx('uploadReusable')}>
          <button type="button" className="asset-modal-backdrop" onClick={closeUploadDialog} aria-label={english ? 'Close' : '关闭'} />
          <section className="asset-modal asset-upload-modal">
            <header><div><span>UPLOAD</span><h2>{uploadResult ? (english ? 'Upload complete' : '上传完成') : tx('uploadReusable')}</h2></div><button type="button" onClick={closeUploadDialog}><X size={17} /></button></header>
            <button type="button" className={`asset-dropzone ${uploadFile ? 'has-file' : ''}`} onClick={() => uploadInputRef.current?.click()}>
              <input ref={uploadInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" onChange={event => chooseUploadFile(event.target.files?.[0] || null)} />
              {uploadFile ? <><Check size={23} /><strong>{uploadFile.name}</strong><span>{Math.ceil(uploadFile.size / 1024)} KB · {english ? 'Click to choose again' : '点击重新选择'}</span></> : <><Upload size={24} /><strong>{english ? 'Choose an image or video file' : '选择图片或视频'}</strong><span>{english ? 'It will be stored in OSS and shown here' : '上传后保存到 OSS，并直接出现在资产中心'}</span></>}
            </button>
            {(uploadResult || (uploadFile && uploadPreviewUrl)) && <div className={`asset-upload-preview ${uploadResult ? 'is-saved' : ''}`} role="status"><AssetPreview interactive compact typeName={assetTypeName(uploadResult || { type: uploadType }, assetTypes)} asset={uploadResult || { type: uploadType, name: uploadName, files: [{ url: uploadPreviewUrl, mime: uploadFile.type }] }} /><div><strong>{uploadResult ? (english ? 'Saved to your assets' : '已保存到我的资产') : (english ? 'File preview' : '文件预览')}</strong><span>{uploadResult ? (english ? 'You can play or inspect it before closing.' : '关闭前可以直接播放或检查画面。') : (english ? 'Confirm this is the file you want to upload.' : '确认无误后再上传保存。')}</span></div></div>}
            <div className="asset-upload-fields">
              <label>{english ? 'Asset name' : '资产名称'}<input value={uploadName} onChange={event => setUploadName(event.target.value)} placeholder={english ? 'A name you will recognize later' : '给以后能认出来的名字'} /></label>
              <div className="asset-field">
                <span>{english ? 'Asset type' : '资产类型'}</span>
                <AssetSelect
                  value={uploadType}
                  onChange={setUploadType}
                  options={uploadTypeOptions}
                  ariaLabel={english ? 'Asset type' : '资产类型'}
                  className="asset-upload-type-select"
                />
              </div>
            </div>
            <p className="asset-upload-note">{english ? 'After upload, you can choose it directly from the Assets panel inside any canvas.' : '上传后，可以在任意画布的资产面板中直接选择。'}</p>
            <footer>{uploadResult ? <><button type="button" onClick={resetUploadForm}>{english ? 'Upload another' : '继续上传'}</button><button type="button" className="is-primary" onClick={closeUploadDialog}>{english ? 'Done' : '完成'}</button></> : <><button type="button" onClick={closeUploadDialog}>{english ? 'Cancel' : '取消'}</button><button type="button" className="is-primary" disabled={!uploadFile || !uploadType || busy === 'upload'} onClick={uploadAsset}>{busy === 'upload' && <Loader2 className="spin" size={14} />}{english ? 'Upload and save' : '上传并保存'}</button></>}</footer>
          </section>
        </div>
      )}
    </div>
  );
}
